/** The papers list and the exam-format card, in the student's words. */
import type { PaperRow, Profile } from "../api/api";
import type { Rules } from "../upload/useTransientPolling";
import { chaptersLabel } from "./chapters";
import { paperFailure } from "../text/errors";

export const LENGTHS: Record<PaperRow["length"], string> = {
  short: "Short paper",
  standard: "Standard paper",
  full: "Full paper",
};

const STALE_MS = 5 * 60_000;

export function isPaperMoving(p: PaperRow): boolean {
  return p.status === "requested" || p.status === "generating";
}

/** A paper is minutes of work; nothing changes faster than this. */
export function paperInterval(_: PaperRow): number {
  return 5000;
}

export const PAPER_RULES: Rules<PaperRow> = { moving: isPaperMoving, interval: paperInterval };

/** Still queued after five minutes: the event was lost, and Retry re-publishes it. */
export function isStale(p: PaperRow, now: number = Date.now()): boolean {
  return p.status === "requested" && now - Date.parse(p.created_at) > STALE_MS;
}

export function describePaper(p: PaperRow): string {
  const head = `${LENGTHS[p.length]} · ${chaptersLabel(p.chapters)}`;
  switch (p.status) {
    case "requested":
      return `${head} · queued`;
    case "generating":
      return p.sections_total
        ? `${head} · writing your paper… a few minutes · section ${p.sections_done ?? 0} of ${
            p.sections_total
          } checked`
        : `${head} · writing your paper… (a few minutes)`;
    case "ready":
      return `${head} · ${p.total_marks} marks · ${p.question_count} questions${
        p.error ? ` · ${p.error}` : ""
      }`;
    case "failed":
      return `${head} · could not be written. ${paperFailure(p.error)}`;
  }
}

export function describeProfile(p: Profile | null, learning: boolean): string {
  if (p === null) {
    return learning
      ? "Learning your board's format from the past paper…"
      : "Upload a past paper to learn your board's format";
  }
  switch (p.status) {
    case "draft":
      return `${p.board || "Format"} learned — check the format before making a paper`;
    case "confirmed":
      return `${p.board || "Format"}${p.paper_title ? ` · ${p.paper_title}` : ""} · confirmed`;
    case "failed":
      return p.error === "no_text_layer"
        ? "That past paper has no text layer — try the PDF from the board's website"
        : "The format could not be learned from that past paper. Try the PDF from the board's website, or another year's paper.";
  }
}
